import React, { useState, useEffect, useCallback } from "react";
import type { PortalPatientProfile } from "../../types/shared";
import PharmacySearchWidget from "../../shared/ui/survey/widgets/PharmacySearchWidget";
import ProviderSearchWidget from "../../shared/ui/survey/widgets/ProviderSearchWidget";
import { FORMATS, formatValue, maskedChange } from "../../shared/lib/inputFormats";
import { canonicalizeProfileNumbers } from "../../lib/canonicalNumbers";

interface InsurerRecord {
  id?: string | number;
  name: string;
  short_name?: string;
  province?: string;
  [key: string]: unknown;
}

function InsurerSearch({ value, onChange }: { value?: string; onChange: (val: string) => void }) {
  const [query, setQuery] = useState(value || "");
  const [allInsurers, setAllInsurers] = useState<InsurerRecord[]>([]);
  const [results, setResults] = useState<InsurerRecord[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/api/insurers/search?q=")
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.insurers) { setAllInsurers(data.insurers); setResults(data.insurers); } })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults(allInsurers); } else {
      const q = query.toLowerCase();
      setResults(allInsurers.filter(ins => ins.name.toLowerCase().includes(q) || ins.short_name?.toLowerCase().includes(q)));
    }
  }, [query, allInsurers]);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowDropdown(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setShowDropdown(true); }}
        onFocus={() => setShowDropdown(true)}
        placeholder="Search insurer..."
        style={{ ...profileInputStyle, width: "100%" }}
      />
      {showDropdown && results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
          backgroundColor: "white", border: "1px solid #d8c9b6", borderTop: "none",
          borderRadius: "0 0 8px 8px", maxHeight: 240, overflowY: "auto",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}>
          {results.map((ins, i) => (
            <div
              key={ins.id || i}
              onClick={() => { setQuery(ins.name); onChange(ins.name); setShowDropdown(false); }}
              style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #ede6dc", fontSize: 13, color: "#182a33", fontWeight: 500 }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f5f0eb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "white"}
            >
              {ins.name}
              {ins.province && <span style={{ fontSize: 11, color: "#5b6e7c", marginLeft: 8 }}>{ins.province}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const profileInputStyle: React.CSSProperties = {
  fontSize: 14, fontWeight: 500, color: "#182a33",
  padding: "6px 10px", border: "1px solid #d8c9b6", borderRadius: 6,
  boxSizing: "border-box", outline: "none",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const inputStyle: React.CSSProperties = {
  fontSize: 14, fontWeight: 500, color: "#182a33",
  padding: "6px 10px", border: "1px solid #d8c9b6", borderRadius: 6,
  width: "100%", boxSizing: "border-box", outline: "none",
  fontFamily: "system-ui, -apple-system, sans-serif",
};

interface EditableRowProps {
  label: string;
  value?: React.ReactNode;
  field: string;
  editing: boolean;
  draft?: string;
  onChange: (field: string, value: string) => void;
  link?: string | null;
  format?: string;
}

function EditableRow({ label, value, field, editing, draft, onChange, link, format }: EditableRowProps) {
  if (editing) {
    const fmt = format && FORMATS[format] ? format : null;
    const displayVal = fmt ? formatValue(fmt, draft ?? "") : (draft ?? "");
    return (
      <div style={{ padding: "6px 0", borderBottom: "1px solid #f5f0eb" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>
          {label}
        </div>
        {fmt ? (
          <input
            type="text"
            value={displayVal as string}
            onChange={(e) => maskedChange(fmt, displayVal, e, (v) => onChange(field, v))}
            placeholder={FORMATS[fmt].placeholder}
            inputMode={FORMATS[fmt].inputMode}
            maxLength={FORMATS[fmt].maxLength}
            autoComplete={FORMATS[fmt].autoComplete}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = "#182a33"}
            onBlur={(e) => e.target.style.borderColor = "#d8c9b6"}
          />
        ) : (
          <input
            value={draft ?? ""}
            onChange={(e) => onChange(field, e.target.value)}
            style={inputStyle}
            onFocus={(e) => e.target.style.borderColor = "#182a33"}
            onBlur={(e) => e.target.style.borderColor = "#d8c9b6"}
          />
        )}
      </div>
    );
  }

  if (!value) return null;
  return (
    <div style={{ padding: "8px 0", borderBottom: "1px solid #f5f0eb" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>
        {label}
      </div>
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14, color: "#182a33", fontWeight: 500, textDecoration: "none" }}>
          {value} ↗
        </a>
      ) : (
        <div style={{ fontSize: 14, color: "#182a33", fontWeight: 500 }}>{value}</div>
      )}
    </div>
  );
}

function SectionCard({ title, children }: { title: React.ReactNode; children?: React.ReactNode }) {
  return (
    <div style={{
      backgroundColor: "#fff",
      borderRadius: 12,
      padding: "16px 20px",
      marginBottom: 16,
      border: "1px solid #e6dccb",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: "#182a33", marginBottom: 12, margin: 0, paddingBottom: 8, borderBottom: "2px solid #e6dccb" }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

interface ProfileSectionProps {
  profile?: PortalPatientProfile | null;
  onProfileSaved?: (profile: PortalPatientProfile) => void;
}

export default function ProfileSection({ profile, onProfileSaved }: ProfileSectionProps) {
  const [insurerWebsite, setInsurerWebsite] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<PortalPatientProfile>({});
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"saved" | "error" | null>(null); // 'saved' | 'error'

  // Look up insurer website from registry
  useEffect(() => {
    const carrier = editing ? draft.insuranceCarrier : profile?.insuranceCarrier;
    if (!carrier) return;
    fetch(`/api/insurers/search?q=${encodeURIComponent(carrier)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const match = data?.results?.[0];
        if (match?.contact?.website) {
          setInsurerWebsite(match.contact.website);
        }
      })
      .catch(() => {});
  }, [profile?.insuranceCarrier]);

  const startEditing = useCallback(() => {
    setDraft({ ...profile });
    setEditing(true);
    setSaveStatus(null);
  }, [profile]);

  const cancelEditing = useCallback(() => {
    setEditing(false);
    setDraft({});
    setSaveStatus(null);
  }, []);

  const onChange = useCallback((field: string, value: string) => {
    setDraft(prev => ({ ...prev, [field]: value }));
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const p = draft;
      const patientId = p.patientId || (p.id as string | undefined) ||
        `${p.firstName}-${p.lastName}-${p.dateOfBirth}`.toLowerCase().replace(/\s+/g, "-");

      const backendProfile = {
        patient_id: patientId,
        first_name: p.firstName,
        last_name: p.lastName,
        date_of_birth: p.dateOfBirth,
        gender: p.gender || null,
        health_care_number: p.healthCardNumber,
        province: p.province,
        phone: p.phone,
        email: p.email,
        address: [p.streetAddress, p.suite].filter(Boolean).join(", "),
        city: p.city,
        postal_code: p.postalCode,
        insurance_provider: p.insuranceCarrier,
        insurance_id: p.memberId,
        group_number: p.groupNumber,
        policy_number: p.policyNumber,
        has_secondary_insurance: p.hasSecondaryInsurance || null,
        secondary_insurance_carrier: p.secondaryInsuranceCarrier || null,
        secondary_policy_number: p.secondaryPolicyNumber || null,
        secondary_member_id: p.secondaryMemberId || null,
        pharmacy_name: p.pharmacyName,
        pharmacy_location: p.pharmacyLocation || "",
        pharmacy_fax: p.pharmacyFax,
        pharmacy_phone: p.pharmacyPhone,
        physician_first_name: p.physicianFirstName,
        physician_last_name: p.physicianLastName,
        physician_city: p.physicianCity,
        physician_phone: p.physicianPhone,
        physician_fax: p.physicianFax,
        authorization_signature: p.signatureName || `${p.firstName} ${p.lastName}`,
        signature_font: p.signatureFont,
        emergency_contact_name: p.emergencyContactName || null,
        emergency_contact_phone: p.emergencyContactPhone || null,
        emergency_contact_relationship: p.emergencyContactRelationship || null,
        preferred_language: p.preferredLanguage || null,
      };

      const res = await fetch("/api/patient/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Canonical numbers on the wire — see lib/canonicalNumbers
        // (imp_11cf2120). formatValue re-spaces them for display on read.
        body: JSON.stringify(canonicalizeProfileNumbers(backendProfile)),
      });

      if (res.ok) {
        const saved = { ...p, id: patientId, patientId };
        localStorage.setItem("parseo_patient_profile", JSON.stringify(saved));
        if (onProfileSaved) onProfileSaved(saved);
        setEditing(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus("error");
      }
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#5b6e7c" }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>👤</div>
        <p style={{ fontSize: 16 }}>No profile set up yet</p>
      </div>
    );
  }

  const p = editing ? draft : profile;
  const fullName = [p.firstName, p.lastName].filter(Boolean).join(" ");
  const address = [p.streetAddress, p.suite, p.city, p.province, p.postalCode].filter(Boolean).join(", ");
  const doctor = p.physicianDisplayName || [p.physicianFirstName, p.physicianLastName].filter(Boolean).join(" ");

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: "#182a33", margin: 0 }}>
            {fullName || "My Profile"}
          </h2>
          <p style={{ fontSize: 13, color: "#5b6e7c", margin: "4px 0 0" }}>
            Your personal and insurance information
            {saveStatus === "saved" && <span style={{ color: "#6b9c8e", marginLeft: 8, fontWeight: 600 }}>Saved</span>}
            {saveStatus === "error" && <span style={{ color: "#c25450", marginLeft: 8, fontWeight: 600 }}>Save failed</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {editing ? (
            <>
              <button
                onClick={cancelEditing}
                style={{
                  padding: "8px 20px", fontSize: 13, fontWeight: 600,
                  backgroundColor: "#fff", color: "#666",
                  border: "1px solid #d8c9b6", borderRadius: 8, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={saveProfile}
                disabled={saving}
                style={{
                  padding: "8px 20px", fontSize: 13, fontWeight: 600,
                  backgroundColor: "#182a33", color: "#fff",
                  border: "none", borderRadius: 8, cursor: saving ? "default" : "pointer",
                  opacity: saving ? 0.6 : 1,
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </>
          ) : (
            <button
              onClick={startEditing}
              style={{
                padding: "8px 20px", fontSize: 13, fontWeight: 600,
                backgroundColor: "#fff", color: "#182a33",
                border: "1px solid #d8c9b6", borderRadius: 8, cursor: "pointer",
              }}
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Personal */}
        <SectionCard title="Personal Information">
          <EditableRow label="First Name" value={p.firstName} field="firstName" editing={editing} draft={p.firstName} onChange={onChange} />
          <EditableRow label="Last Name" value={p.lastName} field="lastName" editing={editing} draft={p.lastName} onChange={onChange} />
          <EditableRow label="Date of Birth" value={p.dateOfBirth} field="dateOfBirth" editing={editing} draft={p.dateOfBirth} onChange={onChange} format="date_iso" />
          <EditableRow label="Gender" value={p.gender} field="gender" editing={editing} draft={p.gender} onChange={onChange} />
          <EditableRow label="Health Card" value={p.healthCardNumber} field="healthCardNumber" editing={editing} draft={p.healthCardNumber} onChange={onChange} format="health_card_ns" />
          <EditableRow label="Language" value={p.preferredLanguage} field="preferredLanguage" editing={editing} draft={p.preferredLanguage} onChange={onChange} />
        </SectionCard>

        {/* Contact */}
        <SectionCard title="Contact">
          <EditableRow label="Phone" value={p.phone} field="phone" editing={editing} draft={p.phone} onChange={onChange} format="phone_na" />
          <EditableRow label="Email" value={p.email} field="email" editing={editing} draft={p.email} onChange={onChange} format="email" />
          <EditableRow label="Street Address" value={editing ? p.streetAddress : address} field="streetAddress" editing={editing} draft={p.streetAddress} onChange={onChange} />
          {editing && (
            <>
              <EditableRow label="City" value={p.city} field="city" editing={editing} draft={p.city} onChange={onChange} />
              <EditableRow label="Province" value={p.province} field="province" editing={editing} draft={p.province} onChange={onChange} />
              <EditableRow label="Postal Code" value={p.postalCode} field="postalCode" editing={editing} draft={p.postalCode} onChange={onChange} format="postal_ca" />
            </>
          )}
          <EditableRow label="Emergency Contact" value={editing ? p.emergencyContactName : (p.emergencyContactName ? `${p.emergencyContactName} (${p.emergencyContactRelationship || ""})` : null)} field="emergencyContactName" editing={editing} draft={p.emergencyContactName} onChange={onChange} />
          {editing && <EditableRow label="Emergency Relationship" value={p.emergencyContactRelationship} field="emergencyContactRelationship" editing={editing} draft={p.emergencyContactRelationship} onChange={onChange} />}
          <EditableRow label="Emergency Phone" value={p.emergencyContactPhone} field="emergencyContactPhone" editing={editing} draft={p.emergencyContactPhone} onChange={onChange} format="phone_na" />
        </SectionCard>

        {/* Insurance */}
        <SectionCard title="Insurance">
          {editing ? (
            <div style={{ padding: "6px 0", borderBottom: "1px solid #f5f0eb" }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>
                Search Carrier
              </div>
              <InsurerSearch value={p.insuranceCarrier} onChange={(val) => onChange("insuranceCarrier", val)} />
            </div>
          ) : (
            <EditableRow label="Carrier" value={p.insuranceCarrier} field="insuranceCarrier" editing={false} draft={p.insuranceCarrier} onChange={onChange} link={insurerWebsite} />
          )}
          <EditableRow label="Member ID" value={p.memberId} field="memberId" editing={editing} draft={p.memberId} onChange={onChange} />
          <EditableRow label="Group Number" value={p.groupNumber} field="groupNumber" editing={editing} draft={p.groupNumber} onChange={onChange} />
          <EditableRow label="Policy Number" value={p.policyNumber} field="policyNumber" editing={editing} draft={p.policyNumber} onChange={onChange} />
          {(editing || (p.hasSecondaryInsurance as unknown) === "Yes") && (
            <>
              <div style={{ marginTop: 8, borderTop: "2px solid #e6dccb", paddingTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px" }}>Secondary Insurance</div>
              </div>
              <EditableRow label="Carrier" value={p.secondaryInsuranceCarrier} field="secondaryInsuranceCarrier" editing={editing} draft={p.secondaryInsuranceCarrier} onChange={onChange} />
              <EditableRow label="Member ID" value={p.secondaryMemberId} field="secondaryMemberId" editing={editing} draft={p.secondaryMemberId} onChange={onChange} />
              <EditableRow label="Policy Number" value={p.secondaryPolicyNumber} field="secondaryPolicyNumber" editing={editing} draft={p.secondaryPolicyNumber} onChange={onChange} />
            </>
          )}
        </SectionCard>

        {/* Pharmacy & Doctor */}
        <SectionCard title="Pharmacy & Doctor">
          {editing ? (
            <>
              <div style={{ padding: "6px 0", borderBottom: "1px solid #f5f0eb" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>
                  Search Pharmacy
                </div>
                <PharmacySearchWidget
                  value={p.pharmacyName || ""}
                  mode="profile"
                  onChange={(val) => onChange("pharmacyName", val)}
                  pharmacyFieldMapping={{ name: "pharmacyName", phone: "pharmacyPhone", fax: "pharmacyFax", address: "pharmacyLocation" }}
                  onBatchFill={(fills) => {
                    setDraft(prev => {
                      const next = { ...prev };
                      Object.entries(fills).forEach(([key, val]) => { next[key] = val; });
                      return next;
                    });
                  }}
                />
              </div>
              <EditableRow label="Pharmacy Location" value={p.pharmacyLocation} field="pharmacyLocation" editing={editing} draft={p.pharmacyLocation} onChange={onChange} />
              <EditableRow label="Pharmacy Phone" value={p.pharmacyPhone} field="pharmacyPhone" editing={editing} draft={p.pharmacyPhone} onChange={onChange} format="phone_na" />
              <EditableRow label="Pharmacy Fax" value={p.pharmacyFax} field="pharmacyFax" editing={editing} draft={p.pharmacyFax} onChange={onChange} format="phone_na" />
            </>
          ) : (
            <>
              <EditableRow label="Pharmacy" value={p.pharmacyName} field="pharmacyName" editing={false} draft={p.pharmacyName} onChange={onChange} />
              <EditableRow label="Pharmacy Location" value={p.pharmacyLocation} field="pharmacyLocation" editing={false} draft={p.pharmacyLocation} onChange={onChange} />
              <EditableRow label="Pharmacy Phone" value={p.pharmacyPhone} field="pharmacyPhone" editing={false} draft={p.pharmacyPhone} onChange={onChange} />
              <EditableRow label="Pharmacy Fax" value={p.pharmacyFax} field="pharmacyFax" editing={false} draft={p.pharmacyFax} onChange={onChange} />
            </>
          )}
          <div style={{ marginTop: 8 }} />
          {editing ? (
            <>
              <div style={{ padding: "6px 0", borderBottom: "1px solid #f5f0eb" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#5b6e7c", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>
                  Search Doctor
                </div>
                <ProviderSearchWidget
                  value={doctor ? `Dr. ${doctor}` : ""}
                  mode="profile"
                  onChange={() => {}}
                  onSelect={(prov) => {
                    setDraft(prev => ({
                      ...prev,
                      physicianFirstName: prov.firstName,
                      physicianLastName: prov.lastName,
                      physicianDisplayName: prov.displayName,
                      physicianPhone: prov.phone,
                      physicianFax: prov.fax,
                      physicianCity: prov.city,
                      physicianSpecialty: prov.specialty,
                    }));
                  }}
                />
              </div>
              <EditableRow label="Doctor First Name" value={p.physicianFirstName} field="physicianFirstName" editing={editing} draft={p.physicianFirstName} onChange={onChange} />
              <EditableRow label="Doctor Last Name" value={p.physicianLastName} field="physicianLastName" editing={editing} draft={p.physicianLastName} onChange={onChange} />
              <EditableRow label="Doctor Phone" value={p.physicianPhone} field="physicianPhone" editing={editing} draft={p.physicianPhone} onChange={onChange} format="phone_na" />
              <EditableRow label="Doctor City" value={p.physicianCity} field="physicianCity" editing={editing} draft={p.physicianCity} onChange={onChange} />
              <EditableRow label="Doctor Fax" value={p.physicianFax} field="physicianFax" editing={editing} draft={p.physicianFax} onChange={onChange} format="phone_na" />
            </>
          ) : (
            <>
              <EditableRow label="Family Doctor" value={doctor ? `Dr. ${doctor}` : null} field="physicianFirstName" editing={false} draft={p.physicianFirstName} onChange={onChange} />
              <EditableRow label="Doctor Phone" value={p.physicianPhone} field="physicianPhone" editing={false} draft={p.physicianPhone} onChange={onChange} />
              <EditableRow label="Doctor City" value={p.physicianCity} field="physicianCity" editing={false} draft={p.physicianCity} onChange={onChange} />
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
